import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_FAMILY,
  formatPence,
  type FamilyAskView,
  type FamilyContent,
  type PartnerView,
} from '@london-life/contracts';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';
import { actionKey, londonDay, londonWhen } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';
import { ChildrenList, RelativesList } from './children-list';
import { NameAsk } from './name-ask';

const STAGE: Record<PartnerView['stage'], string> = {
  dating: '💞 Dating',
  engaged: '💍 Engaged',
  married: '💒 Married',
};

// what an ask is, in a line (a date's spot, the ring, the wedding and where, a baby, a new name)
function askLine(family: FamilyContent, ask: FamilyAskView) {
  if (ask.kind === 'propose') return '💍 Will you marry me?';
  if (ask.kind === 'baby') return `👶 Start a family? The baby would be ${ask.babyName}`;
  if (ask.kind === 'adopt') return `🏡 Adopt together? The child would be ${ask.babyName}`;
  if (ask.kind === 'rename') return `✏️ Call ${ask.childName ?? 'your child'} ${ask.babyName}?`;
  if (ask.kind === 'date') {
    const spot = family.dateSpots.find((item) => item.id === ask.spot);
    return `${spot?.emoji ?? '💕'} ${spot?.label ?? 'A date'}`;
  }
  const tier = family.weddingTiers.find((item) => item.id === ask.tier);
  const venue = family.weddingVenues.find((item) => item.placeId === ask.venue);
  return `${tier?.emoji ?? '💒'} ${tier?.label ?? 'The wedding'} at ${venue?.label ?? 'the venue'}`;
}

// Family, as Lagos Life's 💞 Family: your partners (made in People), what you have done together and your couple
// level, and the next steps: a date, the ring, the wedding, a baby or an adoption. The one who asks pays; the
// money waits until the other says yes, and comes back if they say no or take too long. Then your children and
// your relatives; a nanny on the payroll looks after the babies as soon as the app opens.
export function FamilyScreen(props: PhoneAppProps) {
  const family = useCatalogue()?.family ?? DEFAULT_FAMILY;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.family.mine(), props);
  // which partner's planner is open (a date, the wedding, a baby, an adoption), and what is chosen in it
  const [planning, setPlanning] = useState<{
    lifeId: string;
    what: 'date' | 'wedding' | 'baby' | 'adopt';
  } | null>(null);
  // the nanny, once, when the app opens and a baby needs her
  const nannied = useRef(false);
  useEffect(() => {
    if (!reply?.babies.nanny || nannied.current) return;
    nannied.current = true;
    if (reply.children.some((child) => child.born && (!child.feedFrom || !child.playFrom)))
      void run(() => api.family.nanny(actionKey('nanny')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reply]);
  const [tier, setTier] = useState(family.weddingTiers.find((item) => item.visible)?.id ?? '');
  const [venue, setVenue] = useState(family.weddingVenues.find((item) => item.visible)?.placeId ?? '');
  const [leaving, setLeaving] = useState<string | null>(null);
  const incoming = reply?.asks.filter((ask) => !ask.mine) ?? [];
  const outgoing = reply?.asks.filter((ask) => ask.mine) ?? [];
  const wedding = family.weddingTiers.find((item) => item.id === tier);
  return (
    <PhoneAppFrame
      id="family"
      title="💞 Family"
      className="family-screen"
      closeLabel="Close Family"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Opening Family…'}
    >
      {reply && (
        <div className="business-detail">
          <section className="staff-summary" aria-label="Your family">
            <span aria-hidden="true">{reply.married ? '💒' : '💞'}</span>
            <span>
              <strong>
                {reply.partners.length
                  ? reply.married
                    ? 'Married'
                    : `${reply.partners.length} ${reply.partners.length === 1 ? 'partner' : 'partners'}`
                  : 'No partner yet'}
              </strong>
              <small>
                {reply.partners.length
                  ? reply.married
                    ? 'One spouse at a time: no new partners while you are married.'
                    : `Up to ${reply.maxPartners} partners while dating. Marrying one ends the others.`
                  : 'Grow close to someone, then ask to be partners on their card in People. Dates, the ring and the wedding happen here.'}
                {reply.children.length > 0 &&
                  ` ${reply.children.length} of ${reply.babies.maxChildren} ${reply.babies.maxChildren === 1 ? 'child' : 'children'}.`}
              </small>
            </span>
          </section>

          {incoming.length > 0 && (
            <>
              <strong className="business-step-title">WAITING FOR YOUR ANSWER</strong>
              <ul className="staff-list" aria-label="Waiting for your answer">
                {incoming.map((ask) => (
                  <li key={ask.id}>
                    <span aria-hidden="true">💌</span>
                    <span>
                      <strong>
                        {ask.name}: {askLine(family, ask)}
                      </strong>
                      <small>
                        {ask.pricePence ? `${ask.name} pays ${formatPence(ask.pricePence)} · ` : ''}answer by{' '}
                        {londonWhen(ask.expiresAt)}
                      </small>
                      <span className="cars-actions family-answer">
                        <button
                          type="button"
                          className="city-card-go"
                          disabled={busy}
                          onClick={() => void run(() => api.family.answer(ask.id, true))}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className="flights-terminal"
                          disabled={busy}
                          onClick={() => void run(() => api.family.answer(ask.id, false))}
                        >
                          No
                        </button>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {outgoing.length > 0 && (
            <>
              <strong className="business-step-title">YOU ASKED</strong>
              <ul className="staff-list" aria-label="You asked">
                {outgoing.map((ask) => (
                  <li key={ask.id}>
                    <span aria-hidden="true">⏳</span>
                    <span>
                      <strong>
                        {ask.name}: {askLine(family, ask)}
                      </strong>
                      <small>
                        {ask.pricePence ? `${formatPence(ask.pricePence)} held · ` : ''}until{' '}
                        {londonWhen(ask.expiresAt)}
                      </small>
                      <button
                        type="button"
                        className="flights-terminal"
                        disabled={busy}
                        onClick={() => void run(() => api.family.withdraw(ask.id))}
                      >
                        Take it back
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {reply.partners.length > 0 && <strong className="business-step-title">YOUR PARTNERS</strong>}
          <ul className="staff-list family-list" aria-label="Your partners">
            {reply.partners.map((partner) => {
              const name = partner.username ? `@${partner.username}` : partner.firstName;
              const open = planning?.lifeId === partner.lifeId ? planning.what : null;
              return (
                <li key={partner.lifeId} className={partner.stage}>
                  <span aria-hidden="true">{partner.level.emoji}</span>
                  <span>
                    <strong>
                      {partner.firstName} · {STAGE[partner.stage]}
                    </strong>
                    <small>
                      {name} · together since {londonDay(partner.since)} · closeness {partner.closeness}
                    </small>
                    <em>
                      {partner.level.title} · level {partner.level.level}
                      {partner.level.next !== null ? ` · ${partner.level.next - partner.level.points} to the next` : ''}
                    </em>
                    <span className="family-goals" aria-label={`Couple goals with ${partner.firstName}`}>
                      {family.goals.map((goal) => {
                        const count = partner.counts[goal.key] ?? 0;
                        const next = goal.steps.find((step) => step > count);
                        return (
                          <span key={goal.key}>
                            <span aria-hidden="true">{goal.emoji}</span> {goal.label} {count}
                            {next ? `/${next}` : ' ✓'}
                          </span>
                        );
                      })}
                    </span>

                    <span className="cars-actions">
                      {partner.blocked.date ? (
                        <small className="staff-blocked">💕 {partner.blocked.date}</small>
                      ) : (
                        <button
                          type="button"
                          className="flights-terminal"
                          aria-expanded={open === 'date'}
                          onClick={() => setPlanning(open === 'date' ? null : { lifeId: partner.lifeId, what: 'date' })}
                        >
                          💕 Plan a date…
                        </button>
                      )}
                      {partner.stage === 'dating' &&
                        (partner.blocked.propose ? (
                          <small className="staff-blocked">💍 {partner.blocked.propose}</small>
                        ) : (
                          <button
                            type="button"
                            className="flights-terminal"
                            disabled={busy || reply.walletPence < family.ringPence}
                            onClick={() =>
                              void run(() =>
                                api.family.ask({ kind: 'propose', lifeId: partner.lifeId }, actionKey('family')),
                              )
                            }
                          >
                            💍 Propose · ring {formatPence(family.ringPence)}
                          </button>
                        ))}
                      {partner.stage === 'engaged' &&
                        (partner.blocked.marry ? (
                          <small className="staff-blocked">
                            💒 {partner.blocked.marry}
                            {partner.marryFrom ? ` (from ${londonWhen(partner.marryFrom)})` : ''}
                          </small>
                        ) : (
                          <button
                            type="button"
                            className="flights-terminal"
                            aria-expanded={open === 'wedding'}
                            onClick={() =>
                              setPlanning(open === 'wedding' ? null : { lifeId: partner.lifeId, what: 'wedding' })
                            }
                          >
                            💒 Plan the wedding…
                          </button>
                        ))}
                      {reply.babies.visible &&
                        (partner.blocked.baby ? (
                          <small className="staff-blocked">👶 {partner.blocked.baby}</small>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="flights-terminal"
                              aria-expanded={open === 'baby'}
                              onClick={() =>
                                setPlanning(open === 'baby' ? null : { lifeId: partner.lifeId, what: 'baby' })
                              }
                            >
                              👶 Start a family…
                            </button>
                            <button
                              type="button"
                              className="flights-terminal"
                              aria-expanded={open === 'adopt'}
                              onClick={() =>
                                setPlanning(open === 'adopt' ? null : { lifeId: partner.lifeId, what: 'adopt' })
                              }
                            >
                              🏡 Adopt together…
                            </button>
                          </>
                        ))}
                      {leaving !== partner.lifeId && (
                        <button type="button" className="flights-terminal" onClick={() => setLeaving(partner.lifeId)}>
                          {partner.stage === 'married' ? 'Separate…' : 'Break up…'}
                        </button>
                      )}
                    </span>

                    {open === 'date' && (
                      <ul className="family-options" aria-label={`Dates with ${partner.firstName}`}>
                        {family.dateSpots
                          .filter((spot) => spot.visible)
                          .map((spot) => (
                            <li key={spot.id}>
                              <span>
                                {spot.emoji} {spot.label}
                              </span>
                              <button
                                type="button"
                                className="city-card-go"
                                disabled={busy || reply.walletPence < spot.pricePence}
                                onClick={() =>
                                  void run(() =>
                                    api.family.ask(
                                      { kind: 'date', lifeId: partner.lifeId, spot: spot.id },
                                      actionKey('family'),
                                    ),
                                  ).then(() => setPlanning(null))
                                }
                              >
                                Ask · {spot.pricePence ? formatPence(spot.pricePence) : 'free'}
                              </button>
                            </li>
                          ))}
                      </ul>
                    )}

                    {open === 'wedding' && (
                      <div className="family-wedding">
                        <div role="radiogroup" aria-label="The wedding" className="family-choices">
                          {family.weddingTiers
                            .filter((item) => item.visible)
                            .map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                role="radio"
                                aria-checked={tier === item.id}
                                className={tier === item.id ? 'active' : ''}
                                onClick={() => setTier(item.id)}
                              >
                                <strong>
                                  {item.emoji} {item.label} · {formatPence(item.pricePence)}
                                </strong>
                                <small>
                                  {item.guests} guests: {item.line}
                                </small>
                              </button>
                            ))}
                        </div>
                        <div role="radiogroup" aria-label="Where" className="family-choices">
                          {family.weddingVenues
                            .filter((item) => item.visible)
                            .map((item) => (
                              <button
                                key={item.placeId}
                                type="button"
                                role="radio"
                                aria-checked={venue === item.placeId}
                                className={venue === item.placeId ? 'active' : ''}
                                onClick={() => setVenue(item.placeId)}
                              >
                                <strong>
                                  {item.emoji} {item.label}
                                </strong>
                              </button>
                            ))}
                        </div>
                        <button
                          type="button"
                          className="city-card-go"
                          disabled={busy || !wedding || reply.walletPence < wedding.pricePence}
                          onClick={() =>
                            void run(() =>
                              api.family.ask(
                                { kind: 'marry', lifeId: partner.lifeId, tier, venue },
                                actionKey('family'),
                              ),
                            ).then(() => setPlanning(null))
                          }
                        >
                          Ask {partner.firstName} · {wedding ? formatPence(wedding.pricePence) : ''}
                        </button>
                      </div>
                    )}

                    {(open === 'baby' || open === 'adopt') && (
                      <NameAsk
                        key={open}
                        label={open === 'baby' ? "THE BABY'S NAME" : "THE CHILD'S NAME"}
                        placeholder={open === 'baby' ? 'Ada' : 'Ben'}
                        hint={
                          open === 'baby'
                            ? `${partner.firstName} says yes, then the baby arrives ${Math.round(family.babies.arriveHours / 24)} days later`
                            : `${partner.firstName} says yes, then your child comes home straight away`
                        }
                        cta={`Ask ${partner.firstName}`}
                        busy={busy}
                        onAsk={(name) =>
                          void run(() =>
                            api.family.ask({ kind: open, lifeId: partner.lifeId, name }, actionKey('family')),
                          ).then((done) => done && setPlanning(null))
                        }
                      />
                    )}

                    {leaving === partner.lifeId && (
                      <span className="jet-sell">
                        <span>
                          {partner.stage === 'married' ? 'Separate from' : 'Break up with'} {partner.firstName}? Your
                          couple goals start again from nothing. Your children stay with you both.
                        </span>
                        <button type="button" onClick={() => setLeaving(null)} disabled={busy}>
                          Stay together
                        </button>
                        <button
                          type="button"
                          className="jet-sell-yes"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              const ended = await api.people.act(partner.lifeId, { action: 'end_tie' });
                              const next = await api.family.mine();
                              return { ...next, message: ended.message };
                            }).then(() => setLeaving(null))
                          }
                        >
                          {partner.stage === 'married' ? 'Separate' : 'Break up'}
                        </button>
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>

          <ChildrenList family={family} reply={reply} busy={busy} run={run} />

          <RelativesList
            family={family}
            relatives={reply.relatives}
            busy={busy}
            onRemove={(relative) =>
              void run(async () => {
                const ended = await api.people.act(relative.lifeId, { action: 'end_tie' });
                const next = await api.family.mine();
                return { ...next, message: ended.message };
              })
            }
          />

          <p className="business-muted">
            The one who asks pays when they ask. The money waits until the other says yes, and comes back if they say
            no, take it back, or {family.askHours} hours go by. One date a couple a day. Up to{' '}
            {family.babies.maxChildren} children each; relatives are asked for on their card in People.
          </p>
        </div>
      )}
    </PhoneAppFrame>
  );
}
